// MeshCanvas — Three.js renderer for the body + face mesh.
//
// Sits on top of the video as a transparent WebGL canvas. Two meshes live
// in the scene:
//
//   1. Body mesh — built from the subdivided pose topology in meshTopology.ts.
//      Vertex positions are driven by MediaPipe Pose landmarks (2D for screen
//      space, 3D world coords for depth). Outer-perimeter vertices are
//      optionally snapped to the segmentation-mask silhouette so the mesh
//      conforms to the rider's actual outline.
//
//   2. Face mesh — built from MediaPipe Face Landmarker's 478 landmarks +
//      its canonical triangle tesselation. High-detail surface over the face.
//
// Both meshes use MeshPhongMaterial with vertex normals computed each frame
// from the updated geometry, so the shading smoothly interpolates across
// triangle edges (Gouraud shading) instead of looking faceted.
//
// The scene has three lights: a key directional light, a fill light, and a
// rim back-light for the hologram aesthetic.

import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { VisualizationRefs } from '../hooks/useVisionPipeline';
import {
  SUBDIVIDED_MESH,
  SYNTHETIC_VERTICES,
  ResolvedVertex,
} from '../vision/meshTopology';
import { getFaceMeshTriangles } from '../vision/faceDetector';
import { snapToSilhouette } from '../vision/silhouetteSnap';

interface Props {
  videoRef: React.RefObject<HTMLVideoElement>;
  refs: VisualizationRefs;
  visible: boolean;
}

// World-space depth scale: MediaPipe worldLandmarks are in metres and roughly
// in the range [-1, 1]. We scale z into our normalized 0..1 screen space.
const Z_SCALE = 0.3;

// Vertex indices in the expanded pose vertex array (33 real + 15 synthetic)
// that are on the outer body perimeter and should be silhouette-snapped.
// These are the ones that should follow the actual body outline.
const SILHOUETTE_SNAP_INDICES = new Set<number>([
  7, 8, // ears
  11, 12, // shoulders
  13, 14, // elbows
  15, 16, // wrists
  23, 24, // hips
  25, 26, // knees
  27, 28, // ankles
]);

export default function MeshCanvas({ videoRef, refs, visible }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.OrthographicCamera | null>(null);
  const bodyMeshRef = useRef<THREE.Mesh | null>(null);
  const bodyGeometryRef = useRef<THREE.BufferGeometry | null>(null);
  const faceMeshRef = useRef<THREE.Mesh | null>(null);
  const faceGeometryRef = useRef<THREE.BufferGeometry | null>(null);
  const rafRef = useRef<number | null>(null);

  // Set up Three.js scene once
  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      premultipliedAlpha: false,
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Orthographic camera — our coordinates are in 0..1 normalized screen
    // space with Z as depth. Camera looks down -Z from in front of the scene.
    const camera = new THREE.OrthographicCamera(
      -0.5,
      0.5,
      0.5,
      -0.5,
      -2,
      2
    );
    camera.position.set(0, 0, 1);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Lighting — gives the mesh that shaded-hologram look
    const ambient = new THREE.AmbientLight(0x224455, 0.5);
    scene.add(ambient);

    const keyLight = new THREE.DirectionalLight(0x6aff45, 1.6);
    keyLight.position.set(0.3, 0.5, 1.2);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0x00d9ff, 0.6);
    fillLight.position.set(-0.6, 0, 0.4);
    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xffffff, 0.3);
    rimLight.position.set(0, 0, -1);
    scene.add(rimLight);

    // ----- Body mesh -----
    const bodyGeometry = new THREE.BufferGeometry();
    const bodyMaterial = new THREE.MeshPhongMaterial({
      color: 0x6aff45,
      emissive: 0x0a2a10,
      specular: 0x88ffaa,
      shininess: 60,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.75,
      flatShading: false,
    });
    const bodyMesh = new THREE.Mesh(bodyGeometry, bodyMaterial);
    bodyMesh.frustumCulled = false;
    scene.add(bodyMesh);
    bodyGeometryRef.current = bodyGeometry;
    bodyMeshRef.current = bodyMesh;

    // ----- Face mesh -----
    const faceGeometry = new THREE.BufferGeometry();
    const faceMaterial = new THREE.MeshPhongMaterial({
      color: 0xaaffaa,
      emissive: 0x0a2a10,
      specular: 0xeeffee,
      shininess: 90,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.85,
      flatShading: false,
    });
    const faceMesh = new THREE.Mesh(faceGeometry, faceMaterial);
    faceMesh.frustumCulled = false;
    scene.add(faceMesh);
    faceGeometryRef.current = faceGeometry;
    faceMeshRef.current = faceMesh;

    // Resize handler — uses the canvas's own CSS layout size as source of truth.
    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;
      renderer.setSize(w, h, false);
      const aspect = w / h || 1;
      camera.left = -0.5 * aspect;
      camera.right = 0.5 * aspect;
      camera.top = 0.5;
      camera.bottom = -0.5;
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener('resize', resize);
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      ro.disconnect();
      bodyGeometry.dispose();
      bodyMaterial.dispose();
      faceGeometry.dispose();
      faceMaterial.dispose();
      renderer.dispose();
    };
  }, [videoRef]);

  // Render loop — reads refs each frame and rebuilds geometry
  useEffect(() => {
    if (!visible) {
      // Still render an empty scene so stale geometry isn't visible
      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      const camera = cameraRef.current;
      if (renderer && scene && camera) {
        renderer.clear();
      }
      return;
    }

    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      const camera = cameraRef.current;
      if (!renderer || !scene || !camera) return;

      const landmarks = refs.landmarksRef.current;
      const worldLandmarks = refs.worldLandmarksRef.current;
      const faceLandmarks = refs.faceLandmarksRef.current;
      const edgeMap = refs.edgeMapRef.current;
      const maskW = refs.maskWidthRef.current;
      const maskH = refs.maskHeightRef.current;

      // Update body mesh
      if (landmarks && worldLandmarks && bodyGeometryRef.current) {
        // The camera covers [-0.5*aspect, 0.5*aspect] horizontally so that
        // its view matches the canvas pixels. Mesh X coords must be scaled
        // by the same aspect or the body shrinks to ~1/aspect of canvas
        // width — that's why the mesh used to render as a small mask
        // around the chest instead of filling the rider.
        const canvas = rendererRef.current!.domElement;
        const aspect = canvas.clientWidth / Math.max(1, canvas.clientHeight);
        updateBodyGeometry(
          bodyGeometryRef.current,
          landmarks,
          worldLandmarks,
          edgeMap,
          maskW,
          maskH,
          aspect,
        );
        bodyMeshRef.current!.visible = true;
      } else if (bodyMeshRef.current) {
        bodyMeshRef.current.visible = false;
      }

      // Update face mesh
      if (faceLandmarks && faceGeometryRef.current) {
        updateFaceGeometry(faceGeometryRef.current, faceLandmarks);
        faceMeshRef.current!.visible = true;
      } else if (faceMeshRef.current) {
        faceMeshRef.current.visible = false;
      }

      renderer.render(scene, camera);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [refs, visible]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        display: visible ? 'block' : 'none',
      }}
    />
  );
}

// ----- Body geometry update -----

// Reusable scratch buffers
const BODY_VERTEX_COUNT = 33 + SYNTHETIC_VERTICES.length;
const bodyPositions2D: Array<{ x: number; y: number; vis: number }> = [];
const bodyPositions3D: Array<{ x: number; y: number; z: number }> = [];
let bodyTriangleIndices: number[] | null = null;

// Pre-compute the triangle index list (flat) for the subdivided mesh.
// Each sub-triangle vertex is a weighted combo of expanded vertices, but
// Three.js needs flat vertex arrays. So we expand every sub-triangle into
// 3 unique vertices (no sharing) — ~880 triangles × 3 = ~2640 vertices.
function getBodyTriangleIndices(): number[] {
  if (bodyTriangleIndices) return bodyTriangleIndices;
  bodyTriangleIndices = [];
  for (let i = 0; i < SUBDIVIDED_MESH.length; i++) {
    bodyTriangleIndices.push(i * 3, i * 3 + 1, i * 3 + 2);
  }
  return bodyTriangleIndices;
}

function updateBodyGeometry(
  geometry: THREE.BufferGeometry,
  landmarks: { x: number; y: number; z: number; visibility: number }[],
  worldLandmarks: { x: number; y: number; z: number; visibility: number }[],
  edgeMap: Uint8Array | null,
  maskW: number,
  maskH: number,
  aspect: number = 1,
) {
  // Step 1: expand real + synthetic vertex positions in normalized [0..1]
  // image coords + world-metre z.
  for (let i = 0; i < 33; i++) {
    const l = landmarks[i];
    const wl = worldLandmarks[i];
    bodyPositions2D[i] = {
      x: l.x,
      y: l.y,
      vis: l.visibility ?? 0,
    };
    bodyPositions3D[i] = { x: wl.x, y: wl.y, z: wl.z };
  }
  for (const syn of SYNTHETIC_VERTICES) {
    const n = syn.sources.length;
    const weights = syn.weights ?? syn.sources.map(() => 1 / n);
    const totalW = weights.reduce((s, w) => s + w, 0);
    let x2 = 0, y2 = 0, vis = 1;
    let x3 = 0, y3 = 0, z3 = 0;
    for (let i = 0; i < n; i++) {
      const src = syn.sources[i];
      const w_i = weights[i] / totalW;
      x2 += bodyPositions2D[src].x * w_i;
      y2 += bodyPositions2D[src].y * w_i;
      vis = Math.min(vis, bodyPositions2D[src].vis);
      x3 += bodyPositions3D[src].x * w_i;
      y3 += bodyPositions3D[src].y * w_i;
      z3 += bodyPositions3D[src].z * w_i;
    }
    if (syn.zOffset) z3 += syn.zOffset;
    bodyPositions2D[syn.index] = { x: x2, y: y2, vis };
    bodyPositions3D[syn.index] = { x: x3, y: y3, z: z3 };
  }

  // Step 2: silhouette snap eligible landmarks.
  if (edgeMap && maskW > 0 && maskH > 0) {
    // Compute centroid in mask pixel coords
    let cx = 0, cy = 0, count = 0;
    for (let i = 11; i <= 28; i++) {
      const l = bodyPositions2D[i];
      if (l && l.vis > 0.4) {
        cx += l.x * maskW;
        cy += l.y * maskH;
        count++;
      }
    }
    if (count > 0) {
      cx /= count;
      cy /= count;

      // Build the snap input
      const verticesPx: Array<{ x: number; y: number }> = [];
      const shouldSnap: boolean[] = [];
      for (let i = 0; i < BODY_VERTEX_COUNT; i++) {
        const p = bodyPositions2D[i];
        if (!p) {
          verticesPx.push({ x: 0, y: 0 });
          shouldSnap.push(false);
          continue;
        }
        verticesPx.push({ x: p.x * maskW, y: p.y * maskH });
        shouldSnap.push(
          SILHOUETTE_SNAP_INDICES.has(i) && p.vis > 0.5
        );
      }

      const snapped = snapToSilhouette(
        verticesPx,
        shouldSnap,
        edgeMap,
        maskW,
        maskH,
        cx,
        cy,
        Math.max(30, Math.min(maskW, maskH) * 0.08)
      );
      // Write snapped positions back in normalized coords
      for (let i = 0; i < BODY_VERTEX_COUNT; i++) {
        if (snapped[i].snapped) {
          bodyPositions2D[i].x = snapped[i].x / maskW;
          bodyPositions2D[i].y = snapped[i].y / maskH;
        }
      }
    }
  }

  // Step 3: walk the subdivided mesh, resolve each sub-triangle's 3 vertex
  // positions, and write them into the geometry's position buffer.
  const triCount = SUBDIVIDED_MESH.length;
  const vertexCount = triCount * 3;
  const positions = new Float32Array(vertexCount * 3);

  let vi = 0;
  for (const [va, vb, vc] of SUBDIVIDED_MESH) {
    const a = resolveBodyVertex(va);
    const b = resolveBodyVertex(vb);
    const c = resolveBodyVertex(vc);

    // Skip low-confidence triangles by collapsing them to (0,0,0) — Three.js
    // will still draw them but they'll be degenerate. Cheap and effective.
    const minVis = Math.min(a.vis, b.vis, c.vis);
    if (minVis < 0.4) {
      // zero out the 3 vertices
      positions[vi++] = 0; positions[vi++] = 0; positions[vi++] = 0;
      positions[vi++] = 0; positions[vi++] = 0; positions[vi++] = 0;
      positions[vi++] = 0; positions[vi++] = 0; positions[vi++] = 0;
      continue;
    }

    // Convert normalized image coords to Three.js world coords.
    // Image (0,0) is top-left, Three.js world (0,0) is center. The camera
    // covers [-0.5*aspect, 0.5*aspect] × [-0.5, 0.5], so X must be scaled
    // by aspect to fill the canvas width (otherwise the mesh shrinks to
    // a narrow column).
    //   x: 0..1 → (-0.5..0.5) * aspect
    //   y: 0..1 → 0.5..-0.5 (top-to-bottom, flipped)
    //   z: world metres * Z_SCALE
    positions[vi++] = (a.x - 0.5) * aspect;
    positions[vi++] = -(a.y - 0.5);
    positions[vi++] = -a.z * Z_SCALE;

    positions[vi++] = (b.x - 0.5) * aspect;
    positions[vi++] = -(b.y - 0.5);
    positions[vi++] = -b.z * Z_SCALE;

    positions[vi++] = (c.x - 0.5) * aspect;
    positions[vi++] = -(c.y - 0.5);
    positions[vi++] = -c.z * Z_SCALE;
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  geometry.attributes.position.needsUpdate = true;
}

function resolveBodyVertex(v: ResolvedVertex): {
  x: number;
  y: number;
  z: number;
  vis: number;
} {
  if (v.sources.length === 1) {
    const s2 = bodyPositions2D[v.sources[0]];
    const s3 = bodyPositions3D[v.sources[0]];
    return { x: s2.x, y: s2.y, z: s3.z, vis: s2.vis };
  }
  let x = 0, y = 0, z = 0, vis = 1;
  for (let i = 0; i < v.sources.length; i++) {
    const s2 = bodyPositions2D[v.sources[i]];
    const s3 = bodyPositions3D[v.sources[i]];
    const w = v.weights[i];
    x += s2.x * w;
    y += s2.y * w;
    z += s3.z * w;
    vis = Math.min(vis, s2.vis);
  }
  return { x, y, z, vis };
}

// ----- Face geometry update -----

let faceTriangles: [number, number, number][] | null = null;
let faceIndexArray: Uint16Array | null = null;

function updateFaceGeometry(
  geometry: THREE.BufferGeometry,
  faceLandmarks: { x: number; y: number; z: number }[]
) {
  if (!faceTriangles) {
    faceTriangles = getFaceMeshTriangles();
    // Build a flat index array once for faster geometry updates.
    const flat = new Uint16Array(faceTriangles.length * 3);
    for (let i = 0; i < faceTriangles.length; i++) {
      flat[i * 3] = faceTriangles[i][0];
      flat[i * 3 + 1] = faceTriangles[i][1];
      flat[i * 3 + 2] = faceTriangles[i][2];
    }
    faceIndexArray = flat;
    geometry.setIndex(new THREE.BufferAttribute(flat, 1));
  }

  // Positions: face landmarks are in image-normalized coords, z in relative
  // depth units. Convert the same way as the body mesh.
  const n = faceLandmarks.length;
  const positions = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const lm = faceLandmarks[i];
    positions[i * 3] = lm.x - 0.5;
    positions[i * 3 + 1] = -(lm.y - 0.5);
    // Face z is already relative — scale it similarly to the body z.
    positions[i * 3 + 2] = -lm.z * Z_SCALE * 3;
  }
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  geometry.attributes.position.needsUpdate = true;
}
